/**
 * Which of §9.3's four freshness states a report is in.
 *
 * Split out of `FreshnessBadge` so it can be unit-tested against a fixed clock
 * without rendering anything, which is what §13 asks for.
 */

export type FreshnessState = 'fresh' | 'stale' | 'failed' | 'unconfigured' | 'snapshot'

/**
 * What `data_as_of` means, from the payload (§8.9).
 *
 * `source` is when the data was produced upstream. `snapshot` is when this
 * application last read the source — the only timestamp that exists for a view
 * with no consolidation step behind it. They are rendered with different words
 * on purpose: a badge saying "Data as of 3 hours ago" over a snapshot timestamp
 * would claim the numbers were produced then, which is the misattribution
 * §16.2 was written to prevent.
 */
export type FreshnessKind = 'source' | 'snapshot' | null

export interface FreshnessBadgeProps {
  /** ISO timestamp from the §8.6 payload, or null. */
  readonly dataAsOf: string | null
  /** Why the freshness probe failed, from the payload. */
  readonly error: string | null
  /** Seconds before amber; null when the report has no freshness query. */
  readonly warnAfterSeconds: number | null
  /** What the timestamp means (§8.9). */
  readonly kind?: FreshnessKind
  /** A snapshot report's TTL, which is the age at which it turns amber. */
  readonly snapshotTtlSeconds?: number | null
  /** Called when the reader presses Refresh; absent when there is no control. */
  readonly onRefresh?: (() => void) | undefined
  /** True while a reader-triggered reload is running. */
  readonly refreshing?: boolean
  /** Injectable for deterministic tests. */
  readonly now?: Date
}

export function freshnessState(
  dataAsOf: string | null,
  error: string | null,
  warnAfterSeconds: number | null,
  now: Date = new Date(),
  kind: FreshnessKind = null,
  snapshotTtlSeconds: number | null = null,
): FreshnessState {
  if (error !== null) return 'failed'
  if (kind === 'snapshot' && dataAsOf !== null) {
    // §9.3's fifth state. Its threshold is the snapshot's own TTL, because a
    // snapshot older than the age at which it should have reloaded means the
    // reload has been failing — which is exactly when a reader needs telling.
    const ageSeconds = (now.getTime() - new Date(dataAsOf).getTime()) / 1000
    if (snapshotTtlSeconds !== null && ageSeconds > snapshotTtlSeconds) return 'stale'
    return 'snapshot'
  }
  if (warnAfterSeconds === null) return 'unconfigured'
  if (dataAsOf === null) {
    // The report declares a freshness query, it ran, and the source had no
    // timestamp. Not a failure, but not an answer either.
    return 'unconfigured'
  }
  const ageSeconds = (now.getTime() - new Date(dataAsOf).getTime()) / 1000
  return ageSeconds > warnAfterSeconds ? 'stale' : 'fresh'
}
